<template src="./template.html"/>

<script>
/* eslint-disable */
import _ from 'lodash';
import {directive as onClickaway} from 'vue-clickaway';
import CountryFlag from 'vue-country-flag';
import InputText from '@/components/elements/inputs/text.vue';
import InputSelect from '@/components/elements/inputs/select.vue';
import CountrySelect from '@/components/elements/inputs/countrySelect.vue';
import StateSelect from '@/components/elements/inputs/stateSelect.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import countryList from '@/data/country.js';
import {mapGetters} from "vuex";

export default {
  name: 'AddressBookPicker',
  components: {
    CountryFlag,
    InputSelect,
    InputText,
    Toggle,
    CountrySelect,
    StateSelect,
  },
  directives: {
    onClickaway: onClickaway,
  },
  props: {
    label: {
      type: String,
      default: 'address_1',
    },
    mode: {
      type: String,
      default: 'search'
    },
    searchField: {
      type: String,
      default: 'query'
    },
    placeholder: {
      type: String,
      default: 'Type your address',
    },
    icon: {
      type: String,
      default: 'map-pin'
    },
    disableSearch: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    template: {
      type: String,
      default: '{address_1}, {post_code} {city}'
    },
    rules: {
      type: Object,
      default: () => {
      },
      required: false
    },
    value: {
      type: [Object, String],
      default: () => {
        return {
          address_1: '',
          address_2: '',
          country_id: '',
          post_code: '',
          city: '',
          state: '',
        }
      },
    }
  },
  data() {
    return {
      indexItem: 0,
      service: {},
      placeService: {},
      inputSearch: '',
      isOpened: false,
      hasResults: false,
    };
  },
  computed: {
    ...mapGetters(['get_predictions_api']),
    postCode: {
      // getter
      get: function () {
        if (this.value && typeof this.value === 'object' && this.value.post_code) {
          return String(this.value.post_code).toUpperCase();
        }
        return '';
      },
      // setter
      set: function (newValue) {
        if (this.value && typeof this.value === 'object') {
          this.$emit('input', {
            address_1: this.value.address_1,
            address_2: this.value.address_2,
            country_id: this.value.country_id,
            post_code: String(newValue || '').toUpperCase(),
            city: this.value.city,
            state: this.value.state,
          });
        } else {
          // When bound to a string (common in booking.vue for attention/company), emit the string value safely
          this.$emit('input', String(newValue || ''));
        }
      }
    },
    randomSelectId() {
      return `select-${this.randomNumber()}`
    },
    groupedAddresses() {
      if (this.get_predictions_api.length === 0) return [];

      // Group by customer_id since customer_address records belong to a customer
      const grouped = _.groupBy(this.get_predictions_api, 'customer_id');

      return Object.keys(grouped).map(customerId => {
        const customerAddresses = grouped[customerId];
        const firstRecord = customerAddresses[0];

        // The address comes from the main customer record (stored in customer_address for this API)
        // All customer_address records for same customer_id share the same physical address
        const customerMainAddress = {
          address_1: firstRecord.address_1,
          address_2: firstRecord.address_2,
          post_code: firstRecord.post_code,
          city: firstRecord.city,
          country_id: firstRecord.country_id,
          state: firstRecord.state,
        };

        return {
          addressKey: `customer-${customerId}`,
          address: customerMainAddress,
          customer_id: customerId,
          contacts: customerAddresses.map(contactRecord => ({
            // Basic contact info
            id: contactRecord.id, // customer_address.id
            customer_id: contactRecord.customer_id,
            name: contactRecord.name, // contact name from customer_address
            phone: contactRecord.phone, // contact phone from customer_address
            email: contactRecord.email, // contact email from customer_address
            company_name: contactRecord.company_name,
            vat: contactRecord.vat,
            external_customer_no: contactRecord.external_customer_no,
            customer_type: contactRecord.customer_type,

            // Additional shipping information fields
            has_additional_shipping_info: contactRecord.has_additional_shipping_info,
            goods_incoterm: contactRecord.goods_incoterm,
            duty_paid_by: contactRecord.duty_paid_by,
            duty_payer_account_no: contactRecord.duty_payer_account_no,
            freight_paid_by: contactRecord.freight_paid_by,
            freight_payer_account_no: contactRecord.freight_payer_account_no,
            freight_payer_id: contactRecord.freight_payer_id,
            invoice_type: contactRecord.invoice_type,
            eori: contactRecord.eori,
            eccn: contactRecord.eccn,
            customs_additional_info_text: contactRecord.customs_additional_info_text,
            eei_number: contactRecord.eei_number,
            tin_number: contactRecord.tin_number,
            tin_type: contactRecord.tin_type,
            selected_info_items: contactRecord.selected_info_items,
            selected_info_items_array: contactRecord.selected_info_items_array,
            additional_info_updated_at: contactRecord.additional_info_updated_at,

            // Display name for backward compatibility
            displayName: contactRecord.name || `Contact #${contactRecord.id}`
          }))
        };
      });
    },
    // Fix the naming inconsistency - use margedResultList to match template
    margedResultList() {
      return {
        apiResults: this.get_predictions_api,
        googleResults: [] // AddressBookPicker doesn't use Google results, but template might expect this
      };
    },
    // Also provide mergedResultList for backwards compatibility
    mergedResultList() {
      return this.margedResultList;
    },
  },
  mounted() {
    if (this.value && !this.inputSearch) {
      this.inputSearch = this.value
    }
  },
  methods: {
    emitAdditionalShippingInfo(contact) {
      const additionalInfo = {
        goods_incoterm: contact.goods_incoterm,
        duty_paid_by: contact.duty_paid_by,
        duty_payer_account_no: contact.duty_payer_account_no,
        freight_paid_by: contact.freight_paid_by,
        freight_payer_account_no: contact.freight_payer_account_no,
        freight_payer_id: contact.freight_payer_id,
        invoice_type: contact.invoice_type,
        eori: contact.eori,
        eccn: contact.eccn,
        customs_additional_info_text: contact.customs_additional_info_text,
        eei_number: contact.eei_number,
        tin_number: contact.tin_number,
        tin_type: contact.tin_type,
        selected_info_items_array: contact.selected_info_items_array || []
      };

      // Remove null/undefined values
      Object.keys(additionalInfo).forEach(key => {
        if (additionalInfo[key] === null || additionalInfo[key] === undefined) {
          delete additionalInfo[key];
        }
      });

      console.log('HERE IS THE ADDITIONAL INFO', additionalInfo);

      // Only emit if there's actual additional info
      if (Object.keys(additionalInfo).length > 0) {
        console.log('HERE IS THE ADDITIONAL INFO 2', additionalInfo);
        this.$emit('additionalInfoSelected', {
          customer_id: contact.customer_id,
          additionalInfo: additionalInfo
        });
      }
    },
    getHeaderTitle(customerGroup) {
      // Get the first contact for reference
      const firstContact = customerGroup.contacts[0];
      if (!firstContact) return '';

      // When searching by name (attention field), prioritize showing contact name
      if (this.searchField === 'name') {
        // Show the primary contact name if available
        if (firstContact.name) {
          // If company exists too, you could optionally append it
          return firstContact.company_name
            ? `${firstContact.name} (${firstContact.company_name})`
            : firstContact.name;
        }
        // Fall back to company if no name
        return firstContact.company_name || '';
      }

      // For company search, show company name
      if (this.searchField === 'company_name') {
        return firstContact.company_name || '';
      }

      // Default fallback
      return firstContact.company_name || firstContact.name || '';
    },

    // Keep your existing highlight method but improve it slightly:
    highlight(itemToHighlight) {
      if (!itemToHighlight) return '';

      // Convert to string
      const text = String(itemToHighlight);

      // If no search term, return original
      if (!this.inputSearch || this.inputSearch.trim() === '') {
        return text;
      }

      // Create case-insensitive regex
      let reggie = new RegExp('(' + this.inputSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
      let found = text.search(reggie) !== -1;

      if (!found) return text;

      // Wrap matches in styled span
      return text.replace(reggie, '<span class="highlight-text">$1</span>');
    },
    closeOnBlur(event) {
      if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation();
      }
      setTimeout(() => this.closeSelect(), 150);
    },
    getFlagItem(country_id) {
      if (country_id !== '' && country_id !== undefined) {
        return countryList.filter((opt) => opt.id === country_id.toString())[0].alpha2;
      }
      return '';
    },
    randomNumber() {
      return Math.floor(100000 + Math.random() * 900000);
    },
    nextItemApiList(event) {
      if (event.keyCode == 9) return;

      // Calculate total items for navigation
      let totalItems = 0;
      this.groupedAddresses.forEach(group => {
        totalItems += group.contacts.length;
      });

      if (event.keyCode == 38 && this.indexItem > 0) {
        this.indexItem--;
        // Focus logic for grouped structure
        this.focusItem();
      } else if (event.keyCode == 40 && this.indexItem < totalItems - 1) {
        this.indexItem++;
        // Focus logic for grouped structure
        this.focusItem();
      } else if (event.keyCode == 13) {
        // Click the focused item
        this.clickFocusedItem();
      }
    },

    focusItem() {
      // Helper method to focus the correct item based on indexItem
      let currentIndex = 0;
      for (let groupIndex = 0; groupIndex < this.groupedAddresses.length; groupIndex++) {
        const group = this.groupedAddresses[groupIndex];
        for (let contactIndex = 0; contactIndex < group.contacts.length; contactIndex++) {
          if (currentIndex === this.indexItem) {
            const refName = `${groupIndex}-${contactIndex}-opt-address-book`;
            if (this.$refs[refName] && this.$refs[refName][0]) {
              this.$refs[refName][0].focus();
            }
            return;
          }
          currentIndex++;
        }
      }
    },

    clickFocusedItem() {
      // Helper method to click the focused item
      let currentIndex = 0;
      for (let groupIndex = 0; groupIndex < this.groupedAddresses.length; groupIndex++) {
        const group = this.groupedAddresses[groupIndex];
        for (let contactIndex = 0; contactIndex < group.contacts.length; contactIndex++) {
          if (currentIndex === this.indexItem) {
            this.setAddressWithContact(group.address, group.contacts[contactIndex]);
            return;
          }
          currentIndex++;
        }
      }
    },

    formatAddressDropdown(address) {
      // Create a clean address string
      let addressParts = [];

      if (address.address_1) {
        addressParts.push(address.address_1);
      }

      if (address.post_code && address.city) {
        addressParts.push(`${address.post_code} ${address.city}`);
      } else if (address.city) {
        addressParts.push(address.city);
      } else if (address.post_code) {
        addressParts.push(address.post_code);
      }

      return addressParts.join(', ');
    },

    getContactDisplayName(contact) {
      // Just show the contact name from customer_address record
      return contact.name || `Contact #${contact.id}`;
    },

    setAddressWithContact(address, contact) {
      if (this.mode === 'search') {
        this.inputSearch = '';
      } else {
        this.inputSearch = contact[this.searchField] || this.formatAddressDropdown(address);
      }

      console.log('SETTING ADDRESS WITH CONTACT', {
        address: address,
        contact: contact,
        typeOfAction: 'app'
      });

      // Emit the customer's address with contact info flattened to top level
      this.$emit('addressSelected', {
        ...address, // Customer's main address
        // Flatten contact info to top level for compatibility
        name: contact.name,
        phone: contact.phone,
        email: contact.email,
        company_name: contact.company_name,
        vat: contact.vat,
        customer_id: contact.customer_id,
        contact: contact, // Keep nested contact for backwards compatibility
        typeOfAction: 'app'
      });

      if (contact.has_additional_shipping_info) {
        this.emitAdditionalShippingInfo(contact);
      }

      this.hasResults = false;
    },

    async makeCalls() {
      await this.$store.dispatch('GET_PLACE_API', {
        query: this.inputSearch,
        field: this.searchField
      });
    },

    search: _.debounce(function (event) {
      if (this.disableSearch) {
        this.hasResults = false;
        return;
      }
      if (event.keyCode == 9) return;
      if (event.keyCode == 38 || event.keyCode == 40) {
        if (this.groupedAddresses.length > 0) {
          this.focusItem();
        }
        return;
      }
      if (this.inputSearch != '') {
        this.makeCalls().then(() => {
          if (this.get_predictions_api.length != 0) {
            this.hasResults = true;
          } else {
            this.isOpened = true;
            this.hasResults = false;
          }
        });
      } else {
        this.isOpened = false;
        this.hasResults = false;
      }
    }, 200),

    closeSelect() {
      // Delay closing slightly to avoid race conditions with click and focus changes
      setTimeout(() => {
        this.hasResults = false;
        this.isOpened = false;
      }, 120);
    },
  },
  watch: {
    inputSearch: {
      handler(val) {
        this.$emit('addressBookValChanged', val)
      },
      deep: true
    },
    value: {
      handler(val) {
        if (this.mode === 'search') {
          this.inputSearch = '';
        } else {
          this.inputSearch = val
        }
      },
      deep: true
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/global/variables';
/* Modern Address Book Picker Styles */
.address-book-picker {
  position: relative;
  width: 100%;
}

.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 15px;
  top: 42px;
  color: #6b7280;
  z-index: 2;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1000;
  max-height: 300px;
  max-width: 400px;
  overflow-y: auto;
  margin-top: 4px;
}

.dropdown-content {
  padding: 12px;
}

.address-group {
  margin-bottom: 10px;
}

.address-group:last-child {
  margin-bottom: 0;
}

.address-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0 0 15px;
  margin-bottom: 10px;
}

.address-flag {
  flex-shrink: 0;
  margin-top: 1px;
  padding: 2px;
}

.address-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-left: 15px;
  gap: 2px;
}

.company-name {
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}

.address-text {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.3;
}

.contacts-wrapper {
  margin-left: 40px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
}

.contacts-wrapper::before {
  content: '';
  position: absolute;
  left: -24px;
  top: -10px;
  bottom: 13px;
  width: 1px;
  background: #d1d5db;
}

.contact-item {
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  position: relative;
}

.contact-item::before {
  content: '';
  position: absolute;
  left: -24px;
  top: 50%;
  width: 14px;
  height: 1px;
  background: #d1d5db;
  transform: translateY(-50%);
}

.contact-item:hover {
  background: #f9fafb;
}

.contact-item:focus {
  outline: none;
  background: #eff6ff;
}

.contact-name {
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
  text-decoration: none;
  display: block;
}

.contact-name:hover {
  text-decoration: underline;
}

/* Highlight styles for search matches */
.address-text b,
.contact-name b,
.company-name b {
  background-color: #fef3c7;
  font-weight: 700;
  padding: 1px 2px;
  border-radius: 2px;
}

/* Selected/Active contact state */
.contact-item.selected .contact-name {
  color: #059669;
  font-weight: 500;
}

/* Scrollbar styling */
.dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.dropdown-menu::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive design */
@media (max-width: 640px) {
  .dropdown-menu {
    max-height: 300px;
  }

  .address-text {
    font-size: 14px;
  }

  .contact-name {
    font-size: 13px;
  }

  .company-name {
    font-size: 14px;
  }

  .contacts-wrapper {
    margin-left: 42px;
  }

  .contacts-wrapper::before {
    left: -26px;
  }

  .contact-item::before {
    left: -26px;
  }

  .address-header {
    padding: 6px 0 0 6px;
    gap: 10px;
  }
}

/* Animation for dropdown appearance */
.dropdown-menu {
  animation: dropdownFadeIn 0.15s ease-out;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

::v-deep .highlight-text {
  background-color: rgba(251, 191, 36, 0.15);
  font-weight: 600;
  padding: 0 0px;
  border-radius: 0px;
  color: inherit;
}


/* Keep all your existing styles below */
.address-book-picker {
  position: relative;
  width: 100%;
}

.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 15px;
  top: 42px;
  color: #6b7280;
  z-index: 2;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1000;
  max-height: 300px;
  max-width: 400px;
  overflow-y: auto;
  margin-top: 4px;
}

.dropdown-content {
  padding: 12px;
}

.address-group {
  margin-bottom: 5px;
}

.address-group:last-child {
  margin-bottom: 0;
}

.address-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0 0 15px;
  margin-bottom: 10px;
}

.address-flag {
  flex-shrink: 0;
  margin-top: 1px;
  padding: 2px;
}

.address-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-left: 15px;
  gap: 2px;
}

.company-name {
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}

.address-text {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.3;
}

.contacts-wrapper {
  margin-left: 40px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
}

.contacts-wrapper::before {
  content: '';
  position: absolute;
  left: -24px;
  top: -10px;
  bottom: 13px;
  width: 1px;
  background: #d1d5db;
}

.contact-item {
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  position: relative;
}

.contact-item::before {
  content: '';
  position: absolute;
  left: -24px;
  top: 50%;
  width: 14px;
  height: 1px;
  background: #d1d5db;
  transform: translateY(-50%);
}

.contact-item:hover {
  background: #f9fafb;
}

.contact-item:focus {
  outline: none;
  background: #eff6ff;
}

.contact-name {
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
  text-decoration: none;
  display: block;
}

.contact-name:hover {
  text-decoration: underline;
}

/* Selected/Active contact state */
.contact-item.selected .contact-name {
  color: #059669;
  font-weight: 500;
}

/* Scrollbar styling */
.dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.dropdown-menu::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive design */
@media (max-width: 640px) {
  .dropdown-menu {
    max-height: 300px;
  }

  .address-text {
    font-size: 14px;
  }

  .contact-name {
    font-size: 13px;
  }

  .company-name {
    font-size: 14px;
  }

  .contacts-wrapper {
    margin-left: 42px;
  }

  .contacts-wrapper::before {
    left: -26px;
  }

  .contact-item::before {
    left: -26px;
  }

  .address-header {
    padding: 6px 0 0 6px;
    gap: 10px;
  }
}

/* Animation for dropdown appearance */
.dropdown-menu {
  animation: dropdownFadeIn 0.15s ease-out;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

